import { useState, useEffect, useMemo } from 'react'
import { Modal, Form, Input, Switch, Button, Space, Select, message as antMessage, Tabs, Spin } from 'antd'
import type { TabsProps } from 'antd'
import { Save, X } from 'lucide-react'
import { useProject, useUpdateProject, useCreateProject } from '@/api/projects.api'
import { ResourcesTab } from './ResourcesTab'
import { t } from '@/config/translations'
import type {
  GitLabResource,
  GitHubResource,
  ConfluenceResource,
  JiraResource,
  ProjectConfig,
} from '@/types/api.types'
import styles from './ProjectSettingsModal.module.css'

interface ProjectSettingsModalProps {
  projectId: string | null
  open: boolean
  onClose: () => void
  mode: 'create' | 'edit'
}

export function ProjectSettingsModal({
  projectId,
  open,
  onClose,
  mode,
}: ProjectSettingsModalProps) {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('general')

  // Resources state
  const [gitlabRepos, setGitlabRepos] = useState<GitLabResource[]>([])
  const [githubRepos, setGithubRepos] = useState<GitHubResource[]>([])
  const [confluenceSpaces, setConfluenceSpaces] = useState<ConfluenceResource[]>([])
  const [jiraProjects, setJiraProjects] = useState<JiraResource[]>([])
  const [defaultTags, setDefaultTags] = useState<string[]>([])

  // Collect all unique tags from current resources
  const allResourceTags = useMemo(() => {
    const tags = new Set<string>()
    for (const r of [...gitlabRepos, ...githubRepos]) {
      if (r.tag) tags.add(r.tag)
    }
    for (const r of [...confluenceSpaces, ...jiraProjects]) {
      if (r.tag) tags.add(r.tag)
    }
    return Array.from(tags).sort()
  }, [gitlabRepos, githubRepos, confluenceSpaces, jiraProjects])

  const { data: project, isLoading } = useProject(
    projectId || '',
    !!projectId && mode === 'edit' && open
  )
  const updateMutation = useUpdateProject()
  const createMutation = useCreateProject()

  // Set initial values when project data loads or modal opens
  useEffect(() => {
    if (open && mode === 'edit' && project) {
      form.setFieldsValue({
        id: project.id,
        name: project.name,
        description: project.description || '',
        enabled: project.enabled,
      })
      // Set resources
      setGitlabRepos(project.gitlab_repos ?? [])
      setGithubRepos(project.github_repos ?? [])
      setConfluenceSpaces(project.confluence_spaces ?? [])
      setJiraProjects(project.jira_projects ?? [])
      setDefaultTags(project.default_tags ?? [])
    } else if (open && mode === 'create') {
      form.resetFields()
      form.setFieldsValue({
        enabled: true,
      })
      // Reset resources
      setGitlabRepos([])
      setGithubRepos([])
      setConfluenceSpaces([])
      setJiraProjects([])
      setDefaultTags([])
    }
  }, [open, mode, project, form])

  const handleSubmit = async () => {
    try {
      const values = (await form.validateFields()) as {
        id?: string
        name: string
        description?: string
        enabled?: boolean
      }

      const projectData: ProjectConfig = {
        // In edit mode, use projectId from props (disabled field is not included in validateFields)
        id: mode === 'edit' ? (projectId ?? '') : (values.id ?? ''),
        name: values.name,
        description: values.description ?? '',
        enabled: values.enabled ?? true,
        gitlab_repos: gitlabRepos,
        github_repos: githubRepos,
        confluence_spaces: confluenceSpaces,
        jira_projects: jiraProjects,
        templates: project?.templates ?? [],
        default_tags: defaultTags,
      }

      if (mode === 'create') {
        await createMutation.mutateAsync(projectData)
        void antMessage.success(t('project_created'))
      } else if (projectId) {
        await updateMutation.mutateAsync({ projectId, project: projectData })
        void antMessage.success(t('project_updated'))
      }

      form.resetFields()
      onClose()
    } catch (error) {
      console.error('Failed to save project:', error)
      void antMessage.error(t('connection_error', { error: 'Failed to save project' }))
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const tabItems: TabsProps['items'] = [
    {
      key: 'general',
      label: t('general_settings') || 'General',
      children: (
        <div className={styles['tabContent']}>
          <Form.Item
            name="id"
            label={t('project_id') || 'Project ID'}
            rules={[{ required: true, message: 'Please enter project ID' }]}
          >
            <Input disabled={mode === 'edit'} placeholder="e.g., my-project" />
          </Form.Item>

          <Form.Item
            name="name"
            label={t('project_name') || 'Project Name'}
            rules={[{ required: true, message: 'Please enter project name' }]}
          >
            <Input placeholder="e.g., My Project" />
          </Form.Item>

          <Form.Item name="description" label={t('description') || 'Description'}>
            <Input.TextArea rows={3} placeholder="Project description..." />
          </Form.Item>

          <Form.Item name="enabled" label={t('enabled') || 'Enabled'} valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label={t('default_tags') || 'Default Tags'}>
            <Select
              mode="multiple"
              placeholder={t('default_tags_placeholder') || 'Select tags enabled by default'}
              value={defaultTags}
              onChange={setDefaultTags}
              options={allResourceTags.map((tag) => ({ label: tag, value: tag }))}
              notFoundContent={t('no_tags_hint') || 'Add tags to resources first'}
            />
          </Form.Item>
        </div>
      ),
    },
    {
      key: 'resources',
      label: t('resources'),
      children: (
        <ResourcesTab
          gitlabRepos={gitlabRepos}
          githubRepos={githubRepos}
          confluenceSpaces={confluenceSpaces}
          jiraProjects={jiraProjects}
          onGitlabChange={setGitlabRepos}
          onGithubChange={setGithubRepos}
          onConfluenceChange={setConfluenceSpaces}
          onJiraChange={setJiraProjects}
        />
      ),
    },
  ]

  if (isLoading && mode === 'edit' && open) {
    return (
      <Modal
        title={t('edit_project')}
        open={open}
        onCancel={handleCancel}
        width={700}
        footer={null}
      >
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      title={mode === 'create' ? t('create_project') : t('edit_project')}
      open={open}
      onCancel={handleCancel}
      width={700}
      className={styles['modal'] || undefined}
      footer={
        <Space>
          <Button icon={<X size={16} />} onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            icon={<Save size={16} />}
            onClick={() => {
              void handleSubmit()
            }}
            loading={updateMutation.isPending || createMutation.isPending}
          >
            {t('save')}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          enabled: true,
        }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className={styles['tabs'] || undefined}
        />
      </Form>
    </Modal>
  )
}
